// Per-item AI-suggestion strip. Mechanic-first description display:
// `description_mechanic` is the canonical text the customer signs off on
// (BAR — Title 16 CCR § 3353). The AI's rewrite is shown here as a
// dismissible suggestion the mechanic can adopt or dismiss.
//
// - Renders only when `suggestion` differs from `currentText`.
// - Click anywhere on the strip (or the dedicated paste icon) → PATCH.
// - "x" dismisses for this session only (no PATCH). Both log to BAR audit.
// - On first render per item per session, fires a 'shown' event.

import { useEffect, useRef, useState } from 'react';
import { Box, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconSparkles, IconX } from '@tabler/icons-react';
import { inspectionApi } from '../api/client';

const SUGGEST_BG = 'rgba(168, 85, 247, 0.06)';
const SUGGEST_BORDER = 'rgba(168, 85, 247, 0.28)';
const SUGGEST_FG = '#A855F7';
const TEXT_SECONDARY = '#A1A1AA';

async function sha256Hex(s: string): Promise<string> {
  try {
    const buf = new TextEncoder().encode(s);
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 16);
  } catch {
    return '0';
  }
}

type Props = {
  itemId: number;
  currentText: string;
  suggestion: string;
  onPaste: () => void;
  readOnly?: boolean;
};

export function InspectionSuggestionCard({ itemId, currentText, suggestion, onPaste, readOnly }: Props) {
  const [dismissed, setDismissed] = useState(false);
  const sigRef = useRef<string>('');

  useEffect(() => {
    let cancelled = false;
    sha256Hex(suggestion).then((sha) => { if (!cancelled) sigRef.current = sha; });
    return () => { cancelled = true; };
  }, [suggestion]);

  // Per-impression 'shown' event removed — it fired one POST per visible
  // suggestion card on every items load, which scaled to N requests per
  // page load. The backend just logger.info'd them (no DB write, no
  // analytics ingestion). Impression count, if ever needed, can be
  // computed from the items list at query time. Accept / dismiss events
  // below are user actions (1 per click) and stay.

  if (dismissed) return null;
  if (!suggestion) return null;
  // Show only the sentences the suggestion ADDS — drop any it echoes from the
  // current text. The autofill regeneration ("add add add") often restates the
  // description; without this the card looked duplicated. If it adds nothing
  // new, hide the card so the loop ends instead of showing repeats.
  const splitSents = (s: string) => (s || '').split(/(?<=[.!?])\s+/).map((x) => x.trim()).filter(Boolean);
  const norm = (s: string) => s.replace(/\s+/g, ' ').replace(/[.!?]+$/, '').trim().toLowerCase();
  const _seen = new Set(splitSents(currentText).map(norm));
  const fresh = splitSents(suggestion).filter((s) => !_seen.has(norm(s))).join(' ');
  if (!fresh) return null;

  const handlePaste = () => {
    void inspectionApi.postSuggestionEvent(itemId, { kind: 'accepted', suggestion_sha256: sigRef.current || null });
    onPaste();
    // Close the card right after paste. Without this, the dedup check
    // (suggestion vs currentText) doesn't fire until the SWR cache
    // round-trips, and on append the suggestion is no longer equal to
    // the displayed text — so the card would otherwise linger.
    setDismissed(true);
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    void inspectionApi.postSuggestionEvent(itemId, { kind: 'dismissed', suggestion_sha256: sigRef.current || null });
    // Persist the dismiss: clear the server's description_autofill so the
    // periodic inspection poll (SWR refreshInterval) doesn't re-pull the same
    // suggestion and re-open this card. Was a session-only local hide, which
    // is why a dismissed suggestion kept reappearing.
    void inspectionApi.patchItem(itemId, { description_autofill: '' } as any);
    setDismissed(true);
  };

  return (
    <Box
      mb={6}
      onClick={readOnly ? undefined : handlePaste}
      style={{
        background: SUGGEST_BG,
        border: `1px solid ${SUGGEST_BORDER}`,
        borderRadius: 6,
        padding: '5px 8px 5px 9px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 7,
        cursor: readOnly ? 'default' : 'pointer',
      }}
      role={readOnly ? undefined : 'button'}
      aria-label={readOnly ? undefined : 'Paste AI suggestion'}
    >
      <Tooltip label="Click to paste" position="top" withArrow disabled={readOnly}>
        <IconSparkles size={12} color={SUGGEST_FG} style={{ marginTop: 2, flexShrink: 0 }} />
      </Tooltip>
      <Text size="xs" c={TEXT_SECONDARY} style={{ flex: 1, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
        {fresh}
      </Text>
      <UnstyledButton
        onClick={handleDismiss}
        aria-label="Dismiss suggestion"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: TEXT_SECONDARY,
          padding: 2,
          marginTop: 1,
          flexShrink: 0,
        }}
      >
        <IconX size={11} />
      </UnstyledButton>
    </Box>
  );
}
